import { AssetClass, TransactionDirection } from "@prisma/client";
import { z } from "zod";
import {
  TransactionDbSchema,
  TransactionInputSchema,
} from "../base-transaction/schema";
import { AssetTransactionSchema } from "../transactions/schema";

export const ThirdPartyIdsSchema = z.object({
  coingecko: z.string(),
});

export const BaseAssetSchema = z.object({
  id: z.string().cuid().optional(),
  symbol: z.string(),
  name: z.string(),
  assetClass: z.nativeEnum(AssetClass).default(AssetClass.CRYPTO),
  thirdPartyIds: ThirdPartyIdsSchema.nullish(),
  image: z
    .object({
      small: z.string().url(),
      large: z.string().url(),
      thumb: z.string().url(),
    })
    .nullish(),
});

export const BaseAssetWithQntPriceAmountSchema = BaseAssetSchema.extend({
  quantity: z.coerce.number(),
  price: z.coerce.number(),
  amount: z.coerce.number(),
  direction: z.nativeEnum(TransactionDirection),
});

export const AssetSchema = BaseAssetSchema.extend({
  assetTransactions: z.array(AssetTransactionSchema).optional().default([]),
  transactions: z.array(TransactionDbSchema).optional().default([]),
  portfolioAssetTransactions: z
    .array(
      z.object({
        totalAmount: z.coerce.number(),
        totalFee: z.coerce.number(),
        averageCost: z.coerce.number(),
        totalCost: z.coerce.number(),
      }),
    )
    .optional()
    .default([]),

  trade: z
    .array(
      z.object({
        transaction: TransactionDbSchema,
      }),
    )
    .default([]),
  swaps: z
    .array(
      z.object({
        transaction: TransactionDbSchema,
      }),
    )
    .default([]),
}).transform(({ portfolioAssetTransactions, trade, swaps, ...asset }) => ({
  ...asset,
  tradeQuoteTransactions: trade.map((t) => t.transaction),
  swapQuoteTransactions: swaps.map((t) => t.transaction),
  transactionsTotal: portfolioAssetTransactions.reduce(
    (acc, curr) => {
      return {
        amount: acc.amount + curr.totalAmount,
        cost: acc.cost + curr.totalCost,
        averageCost: acc.averageCost + curr.averageCost,
        fee: acc.fee + curr.totalFee,
      };
    },
    {
      amount: 0,
      cost: 0,
      averageCost: 0,
      fee: 0,
    },
  ),
}));

export const AssetInputSchema = BaseAssetSchema.extend({
  transactions: z.array(TransactionInputSchema).optional().default([]),
});
export type AssetInput = z.infer<typeof AssetInputSchema>;

export function mapAssetToAssetInput(asset: Asset): AssetInput {
  return {
    id: asset.id,
    symbol: asset.symbol,
    name: asset.name,
    assetClass: asset.assetClass,
    thirdPartyIds: asset.thirdPartyIds,
    image: asset.image,
    transactions: [],
  } satisfies AssetInput;
}

export const AssetLatestPricesSchema = z.record(
  z.string(),
  z.object({
    price: z.number(),
    marketCap: z.number(),
    volumeChange: z.number(),
    priceChange: z.number().nullable(),
    lastUpdated: z.number(),
  }),
);

export const AssetRowSchema = AssetSchema;
export type BaseAsset = z.infer<typeof BaseAssetSchema>;
export type BaseAssetWithQntPriceAmount = z.infer<
  typeof BaseAssetWithQntPriceAmountSchema
>;
export type Asset = z.infer<typeof AssetSchema>;
export type AssetRow = z.infer<typeof AssetRowSchema>;
export type AssetLatestPrices = z.infer<typeof AssetLatestPricesSchema>;

export const parseAsset = (raw: unknown): Asset | null => {
  const parsed = AssetSchema.safeParse(raw);
  if (!parsed.success) {
    console.error("Failed to parse asset", {
      asset: raw,
      issues: JSON.stringify(parsed.error.issues, null, 2),
    });
  }

  return parsed.success ? parsed.data : null;
};

const AssetsSchema = z.array(AssetSchema);
export const parseAssets = (raw: unknown): Asset[] => {
  const parsed = AssetsSchema.safeParse(raw);

  return parsed.success ? parsed.data : [];
};

export function parseAssetLatestPrices(data: unknown) {
  const parsedData = AssetLatestPricesSchema.safeParse(data);
  if (!parsedData.success) {
    console.error(
      "Failed to parse asset latest prices",
      parsedData.error.issues,
    );
  }

  return parsedData.success ? parsedData.data : null;
}
